// Add or edit a single HubSpot Unit (Listing) from the Property Viewer's
// Units tab — laid out per the "Edit Unit Dialog" design mock. `unit` null =
// add (prefilled with the property's own street and city/state/zip, since
// most units share them); otherwise edit that unit.
//
// Everything is staged locally — address edits plus the Contacts & Entities
// links/unlinks/role changes (see LinkedRecordsSection / linkStaging) — and
// written in one POST /api/unitMining/manualUnit/save on Save, which builds
// hs_name the same way a mined unit's is built and returns the unit's
// refreshed owners.

import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

import { getAPICall, postAPICall } from "../../../config/apiCalls";
import LinkedRecordsSection from "./LinkedRecordsSection";
import LinkDialogFooter from "./LinkDialogFooter";
import { diffLinks, linksFromRecords, settleLinks, stageAdd, stageRole, stageToggle } from "./linkStaging";
import "./LinkedRecordsDialog.css";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

const ADDRESS_KEYS = ["a1", "a2", "city", "state", "zip"];
const UNIT_KINDS = ["entity", "contact"];
const UNIT_DEFAULT_ROLES = { contact: "Owner", entity: "Corporate Owner" };

const normalize = (value) => (value || "").trim().toLowerCase().replace(/\s+/g, " ");

const addressFromUnit = (unit, property) =>
	unit
		? { a1: unit.address || "", a2: unit.address2 || "", city: unit.city || "", state: unit.state || "", zip: unit.zip || "" }
		: { a1: property?.addressLine1 || "", a2: "", city: property?.city || "", state: property?.state || "", zip: property?.zip || "" };

// Roles and entity types are portal-wide, so they're fetched once per page
// load and shared by every open of the dialog.
let lookupsPromise = null;
const loadLookups = (instance, account) => {
	if (!lookupsPromise) {
		lookupsPromise = Promise.all([
			getAPICall(instance, account, "/api/unitMining/manualUnit/ownerRoles", { kind: "contact" }),
			getAPICall(instance, account, "/api/unitMining/manualUnit/ownerRoles", { kind: "entity" }),
			getAPICall(instance, account, "/api/unitMining/manualUnit/entityTypes").catch(() => ({ data: { options: [] } })),
		])
			.then(([contactRoles, entityRoles, entityTypes]) => ({
				roles: { contact: contactRoles.data.roles || [], entity: entityRoles.data.roles || [] },
				entityTypes: entityTypes.data.options || [],
			}))
			.catch((err) => {
				lookupsPromise = null; // let the next open retry
				throw err;
			});
	}
	return lookupsPromise;
};

const PropertyUnitDialog = ({ open, unit, property, existingUnits, employeeID, onClose, onChanged }) => {
	const { instance, accounts } = useMsal();

	// The unit's HubSpot id — null until an added unit's first Save.
	const [unitId, setUnitId] = useState(unit?.id || null);
	const [address, setAddress] = useState(() => addressFromUnit(unit, property));
	const [savedAddress, setSavedAddress] = useState(() => addressFromUnit(unit, property));
	const [links, setLinks] = useState(() => linksFromRecords(unit));
	const [roles, setRoles] = useState({ contact: [], entity: [] });
	const [entityTypes, setEntityTypes] = useState([]);
	const [saving, setSaving] = useState(false);
	const [savedFlash, setSavedFlash] = useState(false);
	const [errors, setErrors] = useState([]);
	const [notice, setNotice] = useState("");

	useEffect(() => {
		if (!open) return;
		const initial = addressFromUnit(unit, property);
		setUnitId(unit?.id || null);
		setAddress(initial);
		setSavedAddress(initial);
		setLinks(linksFromRecords(unit));
		setSavedFlash(false);
		setErrors([]);
		setNotice("");
		loadLookups(instance, accounts[0])
			.then((lookups) => {
				setRoles(lookups.roles);
				setEntityTypes(lookups.entityTypes);
			})
			.catch((err) => setErrors([err?.response?.data?.error || "Failed to load roles from HubSpot."]));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, unit]);

	const isNew = !unitId;

	// ── Staged-change bookkeeping ───────────────────────────────────────────
	const addressChanged = ADDRESS_KEYS.some((k) => address[k] !== savedAddress[k]);
	const linkDiff = diffLinks(links);
	const statusParts = [...(addressChanged && !isNew ? ["address edited"] : []), ...linkDiff.parts];
	const dirty = isNew || statusParts.length > 0;

	const touch = () => {
		setSavedFlash(false);
		setErrors([]);
		setNotice("");
	};

	const setAddressField = (key) => (e) => {
		const value = e.target.value;
		setAddress((current) => ({ ...current, [key]: value }));
		touch();
	};

	const updateLinks = (fn) => {
		setLinks(fn);
		touch();
	};

	const handleClose = () => {
		if (saving) return;
		onClose();
	};

	const handleSave = () => {
		if (saving || !dirty) return;
		if (!address.a1.trim() || !address.city.trim() || !address.state.trim()) {
			setErrors(["Address 1, City, and State are required."]);
			return;
		}
		// The server's find-or-create only catches an exact HubSpot address
		// match; this catches re-adding a unit already listed on this property.
		const addressKey = `${normalize(address.a1)}|${normalize(address.a2)}`;
		const duplicate = (existingUnits || []).some(
			(u) => String(u.id) !== String(unitId) && `${normalize(u.address)}|${normalize(u.address2)}` === addressKey,
		);
		if (duplicate) {
			setErrors(["A unit with this address is already on this property."]);
			return;
		}

		const payload = {
			hubspotUnitId: unitId,
			propertyId: property?.id,
			fields: isNew || addressChanged ? address : null,
			adds: linkDiff.adds,
			removes: linkDiff.removes,
			roleChanges: linkDiff.roleChanges,
		};

		setSaving(true);
		setErrors([]);
		postAPICall(instance, accounts[0], "/api/unitMining/manualUnit/save", payload, employeeID)
			.then((res) => {
				const { hubspotUnitId, reusedExisting, errors: opErrors = [], owners } = res.data;
				setUnitId(hubspotUnitId);
				setSavedAddress(address);
				// Rebuild from HubSpot's actual state when available, so a
				// partially failed save shows exactly what stuck.
				setLinks((current) => (owners ? linksFromRecords(owners) : settleLinks(current)));
				setErrors(opErrors);
				setNotice(reusedExisting ? "Saved — this address already existed in HubSpot, so that unit is now linked to this property." : "");
				setSavedFlash(opErrors.length === 0);
				onChanged();
			})
			.catch((err) => setErrors([err?.response?.data?.error || err?.message || "Failed to save unit."]))
			.finally(() => setSaving(false));
	};

	const statusText = savedFlash ? notice || "Saved" : dirty && !isNew ? `Unsaved: ${statusParts.join(", ")}` : "";

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ className: "linkDialogPaper" }}>
			<DialogTitle className="propertyViewerEditDialogTitle">
				{isNew ? "Add Unit" : "Edit Unit"}
				{property?.name && (
					<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 400, mt: 0.75 }}>
						{property.name}
					</Typography>
				)}
			</DialogTitle>

			<DialogContent className="linkDialogContent">
				<div className="linkDialogAddress">
					<TextField label="Address 1" size="small" fullWidth value={address.a1} onChange={setAddressField("a1")} disabled={saving} />
					<TextField label="Address 2 (Unit #)" size="small" fullWidth value={address.a2} onChange={setAddressField("a2")} disabled={saving} />
					<div className="linkDialogCityRow">
						<TextField label="City" size="small" fullWidth value={address.city} onChange={setAddressField("city")} disabled={saving} />
						<TextField label="State" size="small" fullWidth value={address.state} onChange={setAddressField("state")} disabled={saving} />
						<TextField label="Zip" size="small" fullWidth value={address.zip} onChange={setAddressField("zip")} disabled={saving} />
					</div>
				</div>

				<LinkedRecordsSection
					title="Contacts & Entities"
					kinds={UNIT_KINDS}
					searchEndpoint="/api/unitMining/manualUnit/ownerSearch"
					searchParams={{ kind: "all" }}
					links={links}
					roles={roles}
					requireRole
					defaultRoleLabels={UNIT_DEFAULT_ROLES}
					entityTypes={entityTypes}
					disabled={saving}
					onAdd={(link) => updateLinks((current) => stageAdd(current, link))}
					onToggle={(key) => updateLinks((current) => stageToggle(current, key))}
					onRoleChange={(key, typeId) => updateLinks((current) => stageRole(current, key, typeId))}
				/>
			</DialogContent>

			<LinkDialogFooter
				statusText={statusText}
				savedFlash={savedFlash}
				errors={errors}
				dirty={dirty}
				saving={saving}
				saveLabel={isNew ? "Add Unit" : "Save"}
				onCancel={handleClose}
				onSave={handleSave}
			/>
		</Dialog>
	);
};

export default PropertyUnitDialog;
